import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { bemCssModules } from '@utils';
import { useNotifications, useNavigate } from '@utils/hooks';

import { useStore, userActions } from '@modules';
import { ProtectNextEnum } from '@modules/types';

import Button from '@components/Button';
import Modal from '@components/NewModal';

import StrongWalletIcon from './security-wallet.svg';

import styles from './StrongPasswordModal.scss';

const cn = bemCssModules(styles, 'StrongPasswordModal');
const { verifyOTP, setupOTP } = ProtectNextEnum;

const StrongPasswordModal: CustomFC = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [description, setDescription] = useState<string>();
  const { strongPasswordNotification } = useNotifications();
  const [{ user }, dispatch] = useStore();

  const hasPassword = !!user.data?.has_password;
  const hasPhone = !!user.data?.phone;
  const hasEmail = !!user.data?.email;
  const isHide = user.isStrongPasswordModal === false || user.data?.strong_security;

  useEffect(() => {
    document.body.style.overflow = isHide ? '' : 'hidden';
  }, [isHide]);

  useEffect(() => {
    if (!hasPassword && !hasPhone) {
      setDescription(t('strong_password.description1'));
    } else if (!hasPassword) {
      setDescription(t('strong_password.description2'));
    } else {
      setDescription(t('strong_password.description3'));
    }
  }, []);

  const handleClose = () => {
    dispatch(userActions.setStrongPasswordModal(false));
  };

  const handleChangeStrongSecurity = () => {
    if (!hasEmail) {
      return navigate<'profileSetEmail'>(CONST.nav.profileSetEmail, {
        state: { strongPassword: true },
      });
    }
    if (!hasPhone) {
      return navigate<'profileSetPhone'>(CONST.nav.profileSetPhone, {
        state: { strongPassword: true },
      });
    }
    if (hasEmail && !hasPassword) {
      return navigate(CONST.nav.profileSetPassword);
    }

    return dispatch(userActions.toggleStrongSecurity({ strong_security: true }))
      .unwrap()
      .then(res => {
        if (res.data.next === setupOTP) {
          navigate(CONST.nav.intro2fa);
        } else if (res.data.next === verifyOTP) {
          navigate<'verifications'>(CONST.nav.verification, {
            state: {
              nextPage: CONST.nav.profile,
              flow: 'ToggleStrongSecurityOTP',
              strongPassword: true,
            },
          });
        } else {
          strongPasswordNotification();
        }
      })
      .catch(err => {
        if (err.data.next === setupOTP) {
          navigate(CONST.nav.intro2fa);
        } else if (err.data.next === verifyOTP) {
          navigate<'verifications'>(CONST.nav.verification, {
            state: {
              nextPage: CONST.nav.profile,
              flow: 'ToggleStrongSecurityOTP',
              strongPassword: true,
            },
          });
        }
        return err;
      });
  };

  const handleClick = () => {
    handleChangeStrongSecurity();
    dispatch(userActions.setStrongPasswordModal(false));
  };

  if (isHide) return null;

  return (
    <div className={cn(null, [className])}>
      <Modal className={cn('modal')} onClose={handleClose} isOpen>
        <div className={cn('content')}>
          <div className={cn('text')}>
            <h1 className={cn('title')}>{t('strong_password.header')}</h1>
            <p className={cn('description')}>{description}</p>

            <Button className={cn('button')} onClick={handleClick}>
              {t('strong_password.button')}
            </Button>
          </div>
          <StrongWalletIcon className={cn('icon')} />
        </div>
      </Modal>
    </div>
  );
};

export default StrongPasswordModal;
